// src/components/common/AccessDenied.tsx
//
// FU-3 — the honest answer when someone reaches a route their access does not
// include.
//
// WHY THIS EXISTS
//   Navigation has been capability-driven for a while, but nothing guarded the
//   ROUTES behind it, and `AppRouter` had no guard component at all. So a
//   direct URL — a bookmark, a shared link, a browser autocomplete, a
//   command-palette jump — mounted the page anyway and the user got whatever
//   the API happened to return: an unstyled error, a blank panel, an empty
//   list that looked like "you have no records", or occasionally a working page
//   the menu had hidden.
//
//   "Page visible → no access" and "empty state that is really a denial" are
//   both worse than a plain statement, because the user cannot tell a
//   permission problem from a bug and has nothing to ask for.
//
// WHAT THIS IS NOT
//   Not authorization. Hiding a route protects nothing — the CRM server is the
//   authority and stays so. This is the UX half: it makes the refusal legible
//   and gives the person somewhere to go.
import React from 'react';
import { Link } from 'react-router-dom';

export interface AccessDeniedProps {
  /** The product-language name of the area, e.g. "Payments". */
  areaLabel: string;
}

export function AccessDenied({ areaLabel }: AccessDeniedProps): React.ReactElement {
  return (
    <div
      // `alert` would interrupt a screen reader mid-navigation for something
      // that is not an emergency. This is the page's content, so it is a
      // region with a heading, reachable the ordinary way.
      role="region"
      aria-labelledby="access-denied-heading"
      data-testid="access-denied"
      className="max-w-lg mx-auto px-4 py-12 text-center"
    >
      <h1 id="access-denied-heading" className="text-xl font-semibold text-gray-900">
        You do not have access to {areaLabel}
      </h1>
      <p className="mt-3 text-sm text-gray-600">
        Your access for this workspace does not include {areaLabel}, which is why
        it is not in your menu. If you need it, ask a workspace owner or
        administrator to grant it.
      </p>
      <p className="mt-2 text-sm text-gray-500">
        Nothing is wrong with the page — you reached it from a link or a saved
        address rather than the menu.
      </p>
      {/* FU-8 is the real fix for this: capabilities come from a snapshot
          taken when the app booted, and the server caches Central's answer for
          up to five minutes, with nothing invalidating either when a grant
          changes. Until Central notifies CRM, a reload is genuinely required
          and a delay is genuinely possible — so the page says so. A "Refresh
          access" button here would be a lie: it could not clear the server's
          cache, so it would often change nothing. */}
      <p className="mt-2 text-sm text-gray-500">
        If your access was changed just now, reload this page. A change can take
        a few minutes to reach the app.
      </p>
      <Link
        to="/"
        className="inline-flex items-center justify-center mt-6 min-h-[44px] px-4 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-700"
      >
        Go to Home
      </Link>
    </div>
  );
}

export default AccessDenied;
